import { useEffect, useState } from 'react';
import type { HealthResponse, PublicModule } from '@hefesto/contracts';

/**
 * Cliente HTTP mínimo. Mismo origen (/api), cookies first-party HttpOnly (credentials: 'same-origin').
 * El navegador nunca ve ni guarda tokens: la sesión vive solo en la cookie.
 * Desde 02A, el Superadmin usa TanStack Query SOBRE este cliente (caché, revalidación e
 * invalidación por señales en vivo; ver pages/admin/queries.ts).
 */
export async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    signal,
    headers: { accept: 'application/json' },
  });
  if (!res.ok && res.status !== 503) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

/** Error normalizado: la API usa { error: { code, … } } y Better Auth { code, message }. */
export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> = {},
    /** Identificador de la petición (para soporte). */
    readonly requestId: string | null = null,
  ) {
    super(message);
    this.name = 'ApiFailure';
  }

  /** Errores de validación por campo (`details.fields`). */
  get fields(): Record<string, string> {
    return (this.details.fields as Record<string, string> | undefined) ?? {};
  }
}

export async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  extraHeaders: Record<string, string> = {},
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      headers:
        method === 'GET'
          ? { accept: 'application/json', ...extraHeaders }
          : { accept: 'application/json', 'content-type': 'application/json', ...extraHeaders },
      body: method === 'GET' ? undefined : JSON.stringify(body ?? {}),
    });
  } catch {
    throw new ApiFailure(0, 'network', 'No pudimos conectar con Hefesto. Revisá tu conexión.');
  }
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    /* respuesta no JSON */
  }
  if (res.ok) return data as T;
  const envelope = (
    data as {
      error?: {
        code?: string;
        message?: string;
        details?: Record<string, unknown>;
        requestId?: string;
      };
    }
  )?.error;
  const flat = data as { code?: string; message?: string } | null;
  throw new ApiFailure(
    res.status,
    envelope?.code ?? flat?.code ?? (res.status === 429 ? 'rate_limited' : 'unknown'),
    envelope?.message ?? flat?.message ?? `HTTP ${res.status}`,
    envelope?.details ?? {},
    envelope?.requestId ?? res.headers.get('x-request-id'),
  );
}

/** PATCH con concurrencia optimista (`If-Match: "<versión>"`, docs/API-CONTRACTS.md §3). */
export const patch = <T = unknown>(path: string, body: unknown, version: number) =>
  request<T>('PATCH', path, body, { 'if-match': `"${version}"` });

export const post = <T = unknown>(path: string, body?: unknown) => request<T>('POST', path, body);

/** DELETE: como toda mutación, va como JSON y con el Origin del mismo sitio (CSRF). */
export const del = <T = unknown>(path: string) => request<T>('DELETE', path);

/** Mensaje claro y neutro para mostrar un fallo de la API (sin detalles internos). */
export function failureMessage(error: unknown): string {
  if (!(error instanceof ApiFailure)) return 'Algo salió mal. Probá de nuevo en unos segundos.';
  if (error.status === 0) return error.message;
  if (error.code === 'rate_limited') return 'Demasiadas peticiones seguidas. Esperá unos segundos.';
  if (error.code === 'mfa_required')
    return 'Esta acción necesita verificar tu identidad con tu app autenticadora.';
  if (error.status >= 500)
    return 'Hefesto no pudo completar la acción. Probá de nuevo en unos minutos.';
  return error.message;
}

/**
 * POST con `Idempotency-Key` (docs/API-CONTRACTS.md §3). La MISMA clave se reutiliza mientras el
 * cuerpo no cambie (doble clic, reintento tras un corte de red → la misma respuesta); si el cuerpo
 * cambia, clave nueva. Una instancia por formulario.
 */
export function idempotentPoster() {
  let last: { body: string; key: string } | null = null;
  return <T = unknown>(path: string, body: unknown) => {
    const serialized = JSON.stringify(body ?? {});
    if (last?.body !== serialized) last = { body: serialized, key: crypto.randomUUID() };
    return request<T>('POST', path, body, { 'idempotency-key': last.key });
  };
}
export const get = <T = unknown>(path: string) => request<T>('GET', path);

export type Remote<T> = { state: 'loading' } | { state: 'ok'; data: T } | { state: 'error' };

export function useRemote<T>(
  path: string,
  parse: (raw: unknown) => T,
  refreshMs?: number,
): Remote<T> {
  const [value, setValue] = useState<Remote<T>>({ state: 'loading' });
  useEffect(() => {
    const ctrl = new AbortController();
    const load = () =>
      getJson<unknown>(path, ctrl.signal)
        .then((raw) => setValue({ state: 'ok', data: parse(raw) }))
        .catch((e: unknown) => {
          if (!ctrl.signal.aborted) setValue({ state: 'error' });
          return e;
        });
    void load();
    const timer = refreshMs ? window.setInterval(load, refreshMs) : undefined;
    return () => {
      ctrl.abort();
      window.clearInterval(timer);
    };
  }, [path, parse, refreshMs]);
  return value;
}

/**
 * El navegador importa SOLO tipos de @hefesto/contracts salvo en formularios, que cargan
 * `@hefesto/contracts/auth` perezosamente (docs/DESIGN-SYSTEM.md §8).
 */
export const parseHealth = (raw: unknown): HealthResponse => {
  const h = raw as HealthResponse;
  if (!h || typeof h.status !== 'string' || typeof h.checks !== 'object')
    throw new Error('Respuesta de salud inesperada');
  return h;
};
export const parseModules = (raw: unknown): PublicModule[] =>
  (raw as { items: PublicModule[] }).items;
